import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { putCartera } from "../helpers/rutaCarteras";

const toYMD = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");
const toISOorNull = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return null;
  const d = new Date(yyyy_mm_dd + "T00:00:00");
  return isNaN(d.getTime()) ? null : d.toISOString();
};
const cleanOrNull = (s) => {
  const t = String(s ?? "").trim();
  return t ? t : null;
};

export default function ModalCarteraEdit({ show, item, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const tipo = useMemo(() => (item?.tipo || "").toUpperCase(), [item]);
  const isCheque = tipo === "CHEQUE";
  const isICheq = tipo === "ICHEQ";

  // identificador visible
  const identValue = useMemo(() => {
    if (!item) return "";
    // priorizamos nuevo "nro", si no hay usamos legacy
    if (item.nro) return String(item.nro || "");
    if (item.tipo === "CHEQUE") return String(item.nroCheque || "");
    if (item.tipo === "ICHEQ") return String(item.idICheq || "");
    return "";
  }, [item]);

  const [form, setForm] = useState({
    tipo: "",
    estado: "EN_CARTERA",
    importe: "",
    recibidoEn: "",

    // ✅ comunes (los 8)
    fechaCheque: "",
    nro: "",

    bancoEmisor: "",

    pagadoA: "",
    cuitPagadoA: "",

    cobradoA: "",
    cuitCobradoA: "",

    fechaPago: "",

    observaciones: "",

    // legacy (compatibilidad)
    nroCheque: "",
    idICheq: "",
    librador: "",
    cuitLibrador: "",
    emisor: "",
    fechaPagoDiferido: "",
    fechaLiquidacion: "",
  });

  useEffect(() => {
    if (!item) return;
    setErr("");

    const t = (item.tipo || "").toUpperCase();

    // unificados con fallback a legacy
    const fechaCheque = item.fechaCheque || item.fechaPagoDiferido || null;

    const nro =
      item.nro ||
      (t === "CHEQUE" ? item.nroCheque : t === "ICHEQ" ? item.idICheq : null);

    const cobradoA =
      item.cobradoA ||
      (t === "CHEQUE" ? item.librador : t === "ICHEQ" ? item.emisor : "");

    const cuitCobradoA = item.cuitCobradoA || item.cuitLibrador || "";

    const fechaPago =
      item.fechaPago || item.fechaLiquidacion || item.fechaPagoDiferido || null;

    setForm({
      tipo: t,
      estado: item.estado || "EN_CARTERA",
      importe: item.importe ?? "",
      recibidoEn: toYMD(item.recibidoEn),

      // ✅ unificados
      fechaCheque: toYMD(fechaCheque),
      nro: nro || "",

      bancoEmisor: item.bancoEmisor || "",

      pagadoA: item.pagadoA || "",
      cuitPagadoA: item.cuitPagadoA || "",

      cobradoA: cobradoA || "",
      cuitCobradoA: cuitCobradoA || "",

      fechaPago: toYMD(fechaPago),

      observaciones: item.observaciones || "",

      // legacy
      nroCheque: item.nroCheque || "",
      idICheq: item.idICheq || "",
      librador: item.librador || "",
      cuitLibrador: item.cuitLibrador || "",
      emisor: item.emisor || "",
      fechaPagoDiferido: toYMD(item.fechaPagoDiferido),
      fechaLiquidacion: toYMD(item.fechaLiquidacion),
    });
  }, [item]);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!item?._id) return;

    setSaving(true);
    setErr("");

    try {
      const t = (form.tipo || "").toUpperCase().trim();
      const nroUnificado = cleanOrNull(form.nro);

      const payload = {
        // base
        estado: form.estado,
        importe: Number(form.importe || 0),
        recibidoEn: toISOorNull(form.recibidoEn),

        // ✅ comunes unificados (los que tu ruta nueva acepta)
        fechaCheque: toISOorNull(form.fechaCheque),
        nro: nroUnificado,
        bancoEmisor: cleanOrNull(form.bancoEmisor),

        pagadoA: cleanOrNull(form.pagadoA),
        cuitPagadoA: cleanOrNull(form.cuitPagadoA),

        cobradoA: cleanOrNull(form.cobradoA),
        cuitCobradoA: cleanOrNull(form.cuitCobradoA),

        fechaPago: toISOorNull(form.fechaPago),

        observaciones: cleanOrNull(form.observaciones),

        // ✅ legacy (compatibilidad + espejo)
        ...(t === "CHEQUE"
          ? {
              nroCheque: nroUnificado,
              librador: cleanOrNull(form.cobradoA),
              cuitLibrador: cleanOrNull(form.cuitCobradoA),
              fechaPagoDiferido: toISOorNull(form.fechaPago),
              // mantenemos también por si hay pantallas viejas
              fechaLiquidacion: toISOorNull(form.fechaPago),
            }
          : {}),
        ...(t === "ICHEQ"
          ? {
              idICheq: nroUnificado,
              emisor: cleanOrNull(form.cobradoA),
              fechaLiquidacion: toISOorNull(form.fechaPago),
            }
          : {}),
      };

      await putCartera(item._id, payload);
      onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.msg || e2?.message || "Error guardando cambios");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Editar Cartera</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {err && <div className="alert alert-danger">{err}</div>}

          <Row className="g-3">
            <Col md={3}>
              <Form.Label>Tipo</Form.Label>
              <Form.Control value={form.tipo} disabled />
            </Col>

            <Col md={3}>
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={form.estado}
                onChange={(e) => setF("estado", e.target.value)}
                disabled={saving}
              >
                <option value="EN_CARTERA">EN_CARTERA</option>
                <option value="DEPOSITADO">DEPOSITADO</option>
                <option value="ACREDITADO">ACREDITADO</option>
                <option value="RECHAZADO">RECHAZADO</option>
                <option value="ANULADO">ANULADO</option>
                <option value="EMITIDO">EMITIDO</option>
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>Importe</Form.Label>
              <Form.Control
                type="number"
                value={form.importe}
                onChange={(e) => setF("importe", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>Recibido en</Form.Label>
              <Form.Control
                type="date"
                value={form.recibidoEn}
                onChange={(e) => setF("recibidoEn", e.target.value)}
                disabled={saving}
              />
            </Col>

            {/* ✅ 8 campos unificados */}
            <Col md={3}>
              <Form.Label>Fecha cheque</Form.Label>
              <Form.Control
                type="date"
                value={form.fechaCheque}
                onChange={(e) => setF("fechaCheque", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>{isCheque ? "Nro Cheque" : "ID iCheq"}</Form.Label>
              <Form.Control
                value={form.nro}
                onChange={(e) => setF("nro", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={6}>
              <Form.Label>Banco emisor</Form.Label>
              <Form.Control
                value={form.bancoEmisor}
                onChange={(e) => setF("bancoEmisor", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={6}>
              <Form.Label>Pagado a (Beneficiario)</Form.Label>
              <Form.Control
                value={form.pagadoA}
                onChange={(e) => setF("pagadoA", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>CUIT Beneficiario</Form.Label>
              <Form.Control
                value={form.cuitPagadoA}
                onChange={(e) => setF("cuitPagadoA", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={6}>
              <Form.Label>
                Cobrado a ({isCheque ? "Librador" : "Emisor"})
              </Form.Label>
              <Form.Control
                value={form.cobradoA}
                onChange={(e) => setF("cobradoA", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>CUIT {isCheque ? "Librador" : "Emisor"}</Form.Label>
              <Form.Control
                value={form.cuitCobradoA}
                onChange={(e) => setF("cuitCobradoA", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={3}>
              <Form.Label>Fecha pago / liquidación</Form.Label>
              <Form.Control
                type="date"
                value={form.fechaPago}
                onChange={(e) => setF("fechaPago", e.target.value)}
                disabled={saving}
              />
            </Col>

            <Col md={9}>
              <Form.Label>Observaciones</Form.Label>
              <Form.Control
                value={form.observaciones}
                onChange={(e) => setF("observaciones", e.target.value)}
                disabled={saving}
              />
            </Col>

            {/* (Opcional) bloque legacy para debug/compat, si querés mantenerlo visible */}
            {/* Si no lo querés ver, borrá este bloque. Mantenerlo no rompe nada. */}
            <Col md={12}>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Nota: este formulario guarda campos unificados (nro/pagadoA/cobradoA/fechaPago) y
                también espeja legacy para compatibilidad.
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
